import { useState, useEffect } from 'react'
import WebApp from '@twa-dev/sdk'
import MainScreen from './components/Screens/MainScreen'
import OrdersScreen from './components/Screens/OrdersScreen'
import ProfileScreen from './components/Screens/ProfileScreen'
import AdminPanel from './components/Screens/AdminPanel'
import DesignLab from './components/DesignLab/DesignLab'
import DebugConsole from './components/Console/DebugConsole'
import BottomNav from './components/BottomNav'
import { Product } from './data/products'
import './App.css'

type Screen = 'home' | 'orders' | 'profile' | 'admin' | 'designLab' | 'console'

function App() {
  const [currentScreen, setCurrentScreen] = useState<Screen>('home')
  const [orders, setOrders] = useState<Product[]>([])
  const [logs, setLogs] = useState<string[]>([])

  useEffect(() => {
    // Инициализация Telegram WebApp
    WebApp.ready()
    WebApp.expand()
    
    addLog('✅ Приложение запущено')
    addLog(`👤 Пользователь: ${WebApp.initDataUnsafe?.user?.first_name || 'Гость'}`)
    
    // Скрываем главную кнопку Telegram
    WebApp.MainButton.hide()
    
    // Загружаем заказы из Telegram CloudStorage
    loadOrdersFromCloud()
    
  }, [])

  const loadOrdersFromCloud = () => {
    try {
      WebApp.CloudStorage.getItem('orders', (error, value) => {
        if (error) {
          addLog(`❌ Ошибка загрузки заказов: ${error}`)
          return
        }
        
        if (value) {
          try {
            const savedOrders = JSON.parse(value) as Product[]
            setOrders(savedOrders)
            addLog(`✅ Загружено заказов из облака: ${savedOrders.length}`)
          } catch (e) {
            addLog('❌ Ошибка парсинга заказов')
          }
        } else {
          addLog('📦 Заказы не найдены в облаке (первый запуск)')
        }
      })
    } catch (error) {
      addLog('❌ CloudStorage недоступен')
    }
  }

  const saveOrdersToCloud = (newOrders: Product[]) => {
    try {
      const ordersJson = JSON.stringify(newOrders)
      WebApp.CloudStorage.setItem('orders', ordersJson, (error, saved) => {
        if (error) {
          addLog(`❌ Ошибка сохранения: ${error}`)
        } else if (saved) {
          addLog(`💾 Заказы сохранены в облако (${newOrders.length} шт)`)
        }
      })
    } catch (error) {
      addLog('❌ Ошибка сохранения в CloudStorage')
    }
  }

  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString('ru-RU')
    setLogs(prev => [...prev, `[${timestamp}] ${message}`])
  }

  const handleAddToCart = (product: Product) => {
    const newOrders = [...orders, product]
    setOrders(newOrders)
    addLog(`✅ Добавлен в заказы: ${product.title}`)
    
    // Сохраняем в Telegram CloudStorage
    saveOrdersToCloud(newOrders)
    
    // Показываем уведомление
    WebApp.showAlert(`Товар "${product.title}" добавлен в заказы!`)
  }

  const handleRemoveOrder = (index: number) => {
    const newOrders = orders.filter((_, i) => i !== index)
    setOrders(newOrders)
    saveOrdersToCloud(newOrders)
    addLog(`🗑️ Заказ удалён из корзины`)
  }

  const handleNavigate = (screen: Screen) => {
    setCurrentScreen(screen)
    addLog(`📍 Переход на: ${screen}`)
  }

  return (
    <div className="app">
      {/* Главный экран */}
      {currentScreen === 'home' && (
        <>
          <MainScreen 
            onLog={addLog}
            onAddToCart={handleAddToCart}
            onNavigate={handleNavigate}
          />
          <BottomNav 
            activeTab="home" 
            onNavigate={(tab) => handleNavigate(tab as Screen)}
          />
        </>
      )}

      {/* Заказы */}
      {currentScreen === 'orders' && (
        <>
          <OrdersScreen 
            onLog={addLog} 
            orders={orders}
            onRemoveOrder={handleRemoveOrder}
          />
          <BottomNav 
            activeTab="orders" 
            onNavigate={(tab) => handleNavigate(tab as Screen)}
          />
        </>
      )}

      {/* Профиль */}
      {currentScreen === 'profile' && (
        <>
          <ProfileScreen orders={orders} />
          <BottomNav 
            activeTab="profile" 
            onNavigate={(tab) => handleNavigate(tab as Screen)}
          />
        </>
      )}

      {/* Админ панель */}
      {currentScreen === 'admin' && (
        <AdminPanel 
          onClose={() => handleNavigate('home')}
          onLog={addLog}
        />
      )}

      {/* Лаборатория дизайна */}
      {currentScreen === 'designLab' && (
        <DesignLab 
          onClose={() => handleNavigate('home')}
          onLog={addLog}
        />
      )}

      {/* Debug консоль */}
      {currentScreen === 'console' && (
        <DebugConsole 
          logs={logs}
          onClose={() => handleNavigate('home')}
          onClear={() => setLogs([])}
        />
      )}

      {/* Кнопки доступа к dev tools (временные) */}
      {(currentScreen === 'home' || currentScreen === 'orders' || currentScreen === 'profile') && (
        <div style={{
          position: 'fixed',
          top: '10px',
          right: '10px',
          display: 'flex',
          gap: '8px',
          zIndex: 1000,
        }}>
          {/* Админка - три точки */}
          <button 
            onClick={() => handleNavigate('admin')}
            style={{
              background: 'rgba(147, 51, 234, 0.15)',
              border: '1px solid rgba(147, 51, 234, 0.3)',
              borderRadius: '8px',
              padding: '8px 12px',
              color: '#a855f7',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            👨‍💼 Admin
          </button>
          <button 
            onClick={() => handleNavigate('designLab')}
            style={{
              background: 'rgba(147, 51, 234, 0.25)',
              border: '1px solid rgba(147, 51, 234, 0.5)',
              borderRadius: '8px',
              padding: '8px 12px',
              color: '#a855f7',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            🎨 Lab
          </button>
          <button 
            onClick={() => handleNavigate('console')}
            style={{
              background: 'rgba(147, 51, 234, 0.25)',
              border: '1px solid rgba(147, 51, 234, 0.5)',
              borderRadius: '8px',
              padding: '8px 12px',
              color: '#a855f7',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            🔧 Debug
          </button>
        </div>
      )}
    </div>
  )
}

export default App
